<template>
  <b-overlay :show="loading">
    <div class="pt-2 pb-2 pl-2 border card shadow max-width-800" style="border-radius:10px; font-weight: 600;">
      <div class="pb-1">
        Report - ICD-10
      </div>

      <b-row>
        <b-col md="3">
          <b-form-datepicker
            :value="dateStart"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="From"
            @input="(val) => dateStart = val"
          />
        </b-col>

        <b-col md="3">
          <b-form-datepicker
            :value="dateEnd"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="To"
            :min="dateStart"
            @input="(val) => dateEnd = val"
          />
        </b-col>

        <b-col md="3" class="pr-2">
          <medical-code-filter-select
            :value="medical_code_id" 
            @change="val => medical_code_id = val"
          />
        </b-col>
      </b-row>
    </div>

    <div>
      <b-row>
        <b-col cols="12">
          <patient-by-region-chart :key="regionKey" :series="regionChartSeries" :chart-options="regionChartOptions"></patient-by-region-chart>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <b-col md="6">
          <patient-by-age-group-chart :series="ageGroupSeries" :chart-options="ageGroupOptions"></patient-by-age-group-chart>
        </b-col>

        <b-col md="6">
          <patient-by-gender-chart
            :title="'Patient Gender'" 
            :series="genderChartSeries" :chart-options="genderChartOptions"
          >
          </patient-by-gender-chart>
        </b-col>
      </b-row>
    </div>

    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :current-page-number="1"
        :show-import-button="false"
        :show-export-button="can('icd_10_reports:CanExport')"
        :show-search="false"
        :total-records="totalRecords"
        @export="onExport"
      >
      </list-table>
    </div>

  </b-overlay>
</template>

<script>
import { debounce, get } from "lodash";

import { 
  BAlert, 
  BAvatar,
  BBadge, 
  BImg, 
  BCard, 
  BCol, 
  BLink, 
  BOverlay, 
  BRow, 
  BFormDatepicker, 
  BCardImg 
} from "bootstrap-vue"

import { startOfMonth } from 'date-fns'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";
import ListTable from "@/@core/components/ListTable/ListTable.vue";

import MedicalCodeFilterSelect from "@core/components/shared/admin/selects/MedicalCodeFilterSelect.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import PatientByGenderChart from "@core/components/shared/admin/reports/view/ReportPieChart.vue"
import PatientByRegionChart from "@core/components/shared/admin/reports/view/RegionChart.vue"
import PatientByAgeGroupChart from "@core/components/shared/admin/reports/view/AgeGroupChart.vue"


const watchHandler = {
  handler(){
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const chartColors = [
  'rgba(7, 125, 188, 1)',
  'rgba(101, 163, 13, 1)',
  'rgba(7, 125, 188, 0.7)',
  'rgba(101, 163, 13, 0.7)',
  'rgba(75, 75, 75, 0.2)',
  'rgba(7, 125, 188, 0.6)',
  'rgba(101, 163, 13, 0.6)',
  'rgba(7, 125, 188, 0.4)',
  'rgba(101, 163, 13, 0.4)',
  'rgba(7, 125, 188, 0.2)'
]

export default {
  components: {
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    vSelect,
    BFormDatepicker,
    PatientByGenderChart,
    MedicalCodeFilterSelect,
    PatientByRegionChart,
    PatientByAgeGroupChart,
    ListTable
  },
  directives: {
    Ripple,
  },
  data(){
    const regionChartOptions = {
      chart: {
        height: 400,
        type: 'area',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        }
      },
      dataLabels: {
        enabled: false,
        formatter: (value) => (value),
      },
      colors: chartColors,
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        lines: {
          show: false
        },
        type: 'string',
        categories: []
      },
      yaxis: {
        lines: {
          show: false
        },
        labels: {
          formatter: (value) => value
        }
      },
      tooltip: {
        x: {
          format: 'MM/yy'
        },
        y: {
          formatter: (value) => value
        }
      },
      legend: {
        position: "top",
        horizontalAlign: 'left'
      }
    }
    const monthBeginning = startOfMonth(new Date())
    return {
      loading: false,
      
      regionChartOptions,
      dateStart: this.formatDate(monthBeginning, 'yyyy-MM-dd'),
      dateEnd: this.formatDate(new Date(), 'yyyy-MM-dd'),
      regionKey: 99,
      regionChartSeries: [10, 10, 10, 10],

      statusFilter: "",
      medical_code_id: null,

      records: [],

      genderChartSeries: [],
      genderChartOptions: {
        chart: {
          type: 'pie',
        },
        colors: ['rgba(7, 125, 188, 1)', 'rgba(101, 163, 13, 1)', 'rgba(7, 125, 188, 0.7)'],
        labels: ['Male - 50', 'Female - 50']
      },

      ageGroupOptions: {},
      ageGroupSeries: [],

      tableColumns: [
        { key: 'fullName', label: "Patient Name", sortable: false, },
        { key: 'date_of_birth', label: "Date of Birth", formatter: val => `${this.formatDate(val, 'dd-MM-yy')}`, sortable: false, },
        { key: 'age', label: "Age", sortable: false },
        { key: 'gender', label: "Gender", sortable: false },
        { key: 'partner', label: "Insurance Partner", sortable: false, },
        { key: 'policy_number', label: "Policy Number", sortable: false, formatter: (val) => val || '-' },
        { key: 'numberOfAppointments', label: "Number of Appointments", sortable: false, },
        { key: 'numberOfFilteredConsultations', label: "Number of Consultations", sortable: false, },
        { key: 'numberOfLabRequests', label: "Number of Labs", sortable: false, },
      ],
      
      debounceFetchListData: null,
      debounceFetchAllCharts: null,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debounceFetchListData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    dateStart: watchHandlerAndResetPageNumber,
    dateEnd: watchHandlerAndResetPageNumber,
    medical_code_id: watchHandlerAndResetPageNumber,
  },
  created() {
    this.debounceFetchListData = debounce(this.fetchListData, 500);
    this.debounceFetchAllCharts = debounce(this.fetchAllCharts, 500);

    this.fetchListData()
    this.fetchAllCharts()

    const {
      from,
      to,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.dateStart = from;
    this.dateEnd = to;
    this.currentPage = +page;
    this.perPage = +limit;
  },
  methods: {
    async fetchListData() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          limit: this.perPage,
          page: this.currentPage,
          medical_code_id: this.medical_code_id,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.icd10Reports.list(query);

        const { data } = request.data;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAllCharts() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          medical_code_id: this.medical_code_id,
        }

        const response = await this.useJwt().mainService.icd10Reports.getChart(query);
        const { regional_chart_data, age_group_data, gender_chart_data } = get(response, 'data.data')
        const series = { name: regional_chart_data.name, data: [] }

        series.data = regional_chart_data.data.map(region_data => region_data.count)
        this.regionChartOptions.xaxis.categories = regional_chart_data.data.map(region_data => {
          const region = region_data._id;
          if (region.includes("Region")){
            return region.replace("Region", "")
          }
          return region_data._id
        })
        this.regionChartSeries = [series]
        this.regionKey += 1
        if (Array.isArray(age_group_data.data) && age_group_data.data.length){
          this.ageGroupSeries = [{
            name: age_group_data.name,
            data: Object.values(age_group_data.data[0])
          }];
          this.ageGroupOptions = {
            xaxis: {
              categories: Object.keys(age_group_data.data[0])
            },
            colors: chartColors,
          }
        } else {
          this.ageGroupSeries = []
          this.ageGroupOptions = {}
        }
        this.genderChartSeries = gender_chart_data.data.map(gender_data => gender_data.count);
        this.genderChartOptions = {
          labels: gender_chart_data.data.map(gender_data => gender_data._id[0].toUpperCase() + gender_data._id.slice(1))
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          medical_code_id: this.medical_code_id,
        }

        const response = await this.useJwt().mainService.icd10Reports.export(query);
        FileDownload(response.data, "icd-10-report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    }, 
  }
}
</script>

<style scoped>

</style>

